import React, { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Nav, Row } from "react-bootstrap";

function SLMBrowsePager({ idList = [], currentRecordId, onNavigate, elementIds = {} }) {
  const normalizedList = useMemo(
    () => (Array.isArray(idList) ? idList.map((id) => Number(id)).filter((id) => Number.isFinite(id)) : []),
    [idList]
  );

  const [currentIndex, setCurrentIndex] = useState(null);
  const [currentInput, setCurrentInput] = useState("");

  useEffect(() => {
    const numericCurrentId = Number(currentRecordId);
    if (!Number.isFinite(numericCurrentId) || normalizedList.length === 0) {
      setCurrentIndex(null);
      setCurrentInput("");
      return;
    }

    const idx = normalizedList.indexOf(numericCurrentId);
    if (idx < 0) {
      setCurrentIndex(null);
      setCurrentInput("");
      return;
    }

    setCurrentIndex(idx);
    setCurrentInput(String(idx + 1));
  }, [currentRecordId, normalizedList]);

  const previousId = currentIndex !== null && currentIndex > 0 ? normalizedList[currentIndex - 1] : null;
  const nextId =
    currentIndex !== null && currentIndex + 1 < normalizedList.length ? normalizedList[currentIndex + 1] : null;

  const handleCurrentIndexChange = (event) => {
    const value = String(event.target.value || "").replace(/[^0-9]/g, "");

    if (value === "") {
      setCurrentInput("");
      return;
    }

    if (normalizedList.length === 0) {
      return;
    }

    const parsed = Number(value);
    const clamped = Math.min(Math.max(parsed, 1), normalizedList.length);
    setCurrentInput(String(clamped));
    setCurrentIndex(clamped - 1);
  };

  const handleGoToRecord = () => {
    if (normalizedList.length === 0) {
      return;
    }

    const digitsOnly = String(currentInput || "").replace(/[^0-9]/g, "");
    if (digitsOnly === "") {
      if (currentIndex !== null && currentIndex >= 0) {
        setCurrentInput(String(currentIndex + 1));
      }
      return;
    }

    const parsed = Number(digitsOnly);
    if (!Number.isFinite(parsed)) {
      if (currentIndex !== null && currentIndex >= 0) {
        setCurrentInput(String(currentIndex + 1));
      }
      return;
    }

    const clamped = Math.min(Math.max(parsed, 1), normalizedList.length);
    const nextIndex = clamped - 1;
    const targetId = normalizedList[nextIndex];

    setCurrentInput(String(clamped));
    setCurrentIndex(nextIndex);

    if (Number(targetId) === Number(currentRecordId)) {
      return;
    }

    if (typeof onNavigate === "function") {
      onNavigate(targetId, normalizedList);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleGoToRecord();
    }
  };

  if (normalizedList.length === 0 || currentIndex === null) {
    return null;
  }

  return (
    <Nav className="justify-content-lg-center justify-content-start align-items-center flex-nowrap gap-1">
      {previousId !== null && (
        <Button
          id={elementIds.previousButtonId}
          type="button"
          variant="info"
          size="sm"
          onClick={() => onNavigate(previousId, normalizedList)}
        >
          Previous
        </Button>
      )}
      {nextId !== null && (
        <Button
          id={elementIds.nextButtonId}
          type="button"
          variant="info"
          size="sm"
          onClick={() => onNavigate(nextId, normalizedList)}
        >
          Next
        </Button>
      )}
      <Form.Group as={Row} controlId="currentIdIndex" className="mb-0 align-items-center flex-nowrap">
        <Col className="zeroPadRight">
          <Form.Control
            id={elementIds.inputId}
            onChange={handleCurrentIndexChange}
            value={currentInput}
            as="input"
            type="text"
            min="1"
            className="pageCount"
            onBlur={handleGoToRecord}
            onKeyDown={handleKeyDown}
          />
        </Col>
        <Col className="zeroPadLeft d-flex align-items-center">
          <span className="browseCountText">of {normalizedList.length}</span>
        </Col>
      </Form.Group>
    </Nav>
  );
}

export default SLMBrowsePager;
